/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import { Icon } from '../../Icon';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import {
  selectContainerCss,
  selectedLabelCss,
  labelWithoutPlaceholderCss,
  placeholderCss,
} from './index.styles';
import {
  labelCss,
  errorCss,
  selectIconCss,
  selectInputCss,
  errorContainerCss,
} from '../baseStyles/index.styles';

export const mapDropdownValues = (sitecoreField = [], isIDValue) =>
  sitecoreField.map(dropdownField => {
    const value = get(dropdownField, 'fields.Label.value', '');
    const id = get(dropdownField, 'id', '');

    return { value: isIDValue ? id : value, label: value };
  });

const getSelectedLabel = (props, index) => {
  return get(props, `options[${index}].label`, '');
};

const renderHTML = rawHTML =>
  React.createElement('div', {
    dangerouslySetInnerHTML: { __html: rawHTML },
  });

class Select extends SafePureComponent {
  constructor(props) {
    super(props);

    const { placeholder } = props;
    const hasPlaceholder = Boolean(placeholder);

    this.state = {
      selectedLabel: hasPlaceholder ? '' : getSelectedLabel(props, 0),
      options: get(props, 'options', []),
      validOptionSelected: false,
      focus: false,
      shrink: false,
    };

    this.selectRef = React.createRef();
  }

  componentDidMount() {
    const { defaultValue, defaultLabel } = this.props;

    if (defaultLabel) {
      this.setState({ selectedLabel: defaultLabel });
    } else if (defaultValue) {
      this.setState({ selectedLabel: defaultValue });
    }

    const shouldShrink =
      this.state.shrink || this.state.selectedLabel.length > 0;
    this.setState({
      shrink: shouldShrink,
      validOptionSelected: this.getValidOptionSelected(this.state.options),
    });
  }

  componentDidUpdate(_previousProp, previousState) {
    if (previousState.selectedLabel !== this.state.selectedLabel) {
      const shouldShrink =
        this.state.shrink || this.state.selectedLabel.length > 0;
      this.setState({
        shrink: shouldShrink,
        validOptionSelected: this.getValidOptionSelected(this.state.options),
      });
    }
  }

  getValidOptionSelected(options) {
    const selectedValueIndex = this.getSelectedValueIndex(options);
    return selectedValueIndex > -1;
  }

  onSelectChange = event => {
    const index = this.selectRef.current.selectedIndex;
    const selectedLabel = getSelectedLabel(
      this.props,
      this.state.validOptionSelected ? index : index - 1,
    );

    this.setState({ selectedLabel });
    this.props.onChangeProp(event, selectedLabel, index);
  };

  onFocusSelect = () => {
    this.setState({ focus: true, shrink: true });
    const { onFocusSelect } = this.props;
    if (onFocusSelect) {
      onFocusSelect();
    }
  };

  onBlurSelect = () => {
    const shouldShrink = this.state.selectedLabel.length > 0;
    this.setState({ focus: false, shrink: shouldShrink });
  };

  getSelectedValueIndex(options) {
    return findIndex(options, ['label', this.state.selectedLabel]);
  }

  safeRender() {
    const { selectedLabel, shrink, focus } = this.state;
    const {
      className,
      customIconCss,
      customLabelCss,
      customPlaceholderCss,
      customInputCss,
      disablePlaceholder,
      error,
      errorMessage,
      iconCss,
      id,
      // placeholder,
      selectProps = {},
    } = this.props;
    let { placeholder } = this.props;

    const options = get(this.props, 'options', []);
    const hasPlaceholder = Boolean(placeholder);
    const selectedValueIndex = this.getSelectedValueIndex(options);

    placeholder = placeholder.split('*').join(' <span>*</span>');
    placeholder = renderHTML(placeholder);

    return (
      <React.Fragment>
        <div css={selectContainerCss} className={className}>
          {hasPlaceholder && (
            <label
              css={[labelCss, placeholderCss, customPlaceholderCss]}
              data-shrink={shrink}
              data-focus={focus}
              data-error={error}
              htmlFor={id}
              id={`${id}-label`}
            >
              {placeholder}
            </label>
          )}
          <span
            css={[
              selectedLabelCss,
              !hasPlaceholder && labelWithoutPlaceholderCss,
              customLabelCss,
            ]}
            data-shrink={shrink}
            data-focus={focus}
            data-error={error}
          >
            {selectedLabel}
            <Icon
              css={iconCss}
              name="chevron-down"
              customCss={[selectIconCss, customIconCss]}
            />
          </span>
          <select
            css={[selectInputCss, customInputCss]}
            name={id}
            ref={this.selectRef}
            onChange={this.onSelectChange}
            onFocus={this.onFocusSelect}
            onBlur={this.onBlurSelect}
            {...selectProps}
          >
            {!this.state.validOptionSelected && (
              <option
                // disabled
                hidden
                disabled={disablePlaceholder}
                selected={selectedValueIndex === -1}
              ></option>
            )}
            {options.map(({ label, value }, index) => (
              <option
                key={index}
                value={value}
                selected={selectedValueIndex === index}
              >
                {label}
              </option>
            ))}
          </select>
        </div>
        <div css={errorContainerCss}>
          <span id={`${id}-helper-text`} css={errorCss}>
            {error && errorMessage}
          </span>
        </div>
      </React.Fragment>
    );
  }
}

Select.displayName = 'Select';

Select.propTypes = {
  customIconCss: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  customLabelCss: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  customPlaceholderCss: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  customInputCss: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disablePlaceholder: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  onChangeProp: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
};

Select.defaultProps = {
  customIconCss: {},
  customLabelCss: {},
  customPlaceholderCss: {},
  defaultValue: '',
  disablePlaceholder: false,
  error: false,
  errorMessage: '',
  onChangeProp: () => {},
  options: [],
  placeholder: '',
};

export default Select;
